'use client';

import { Download } from 'lucide-react';
import { useState } from 'react';
import { useLocale } from '@/components/common/locale-provider';
import { BackButton } from '@/components/layout/page-layout';
import { toast } from '@/components/shared/toast';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { GridPattern } from '@/components/ui/grid-pattern';
import { TooltipProvider } from '@/components/ui/tooltip';
import { useTranslations } from '@/hooks/use-translations';
import { PLUGIN_DOWNLOADS, type PluginDownload } from '@/lib/downloads/config';
import { cn } from '@/lib/utils';
import { usePluginDownloadAction } from './use-plugin-download-action';

export function LegacyDownloadsPageContent() {
  const { locale } = useLocale();
  const { t } = useTranslations(locale);
  const [copyFailedDownloadIds, setCopyFailedDownloadIds] = useState<
    Set<PluginDownload['id']>
  >(() => new Set());
  const { canOpenExternal, isRhinoHost, startDownload } =
    usePluginDownloadAction();

  const handleDownload = async (download: PluginDownload) => {
    const outcome = await startDownload(download);
    if (outcome === 'link_copied') {
      setCopyFailedDownloadIds((current) => {
        const next = new Set(current);
        next.delete(download.id);
        return next;
      });
      toast.success(t('downloads.rhinoEmbedded.linkCopied'));
    } else if (outcome === 'copy_failed') {
      setCopyFailedDownloadIds((current) => new Set(current).add(download.id));
      toast.error(t('downloads.rhinoEmbedded.copyFailed'));
    }
  };

  return (
    <section className="relative min-h-screen overflow-hidden bg-muted">
      <div className="absolute top-4 left-4 z-10">
        <TooltipProvider>
          <BackButton href="/" />
        </TooltipProvider>
      </div>
      <GridPattern
        width={30}
        height={30}
        strokeDasharray="4 4"
        className={cn(
          'mask-[radial-gradient(700px_circle_at_center,white,transparent)]',
          'fill-muted-foreground/20 stroke-muted-foreground/20 dark:fill-muted-foreground/20 dark:stroke-muted-foreground/20',
        )}
      />
      <div className="relative flex min-h-screen items-center justify-center px-4 py-12">
        <div className="flex w-full max-w-3xl flex-col items-center gap-y-8">
          <div className="flex flex-col items-center gap-y-4 text-center">
            <div className="flex items-center gap-1">
              <span className="text-4xl font-semibold">
                <span className="relative top-[0.15em] inline-block origin-center rotate-180">
                  v
                </span>
                rkyv
              </span>
            </div>
            <div className="space-y-2">
              <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
                {t('downloads.pageTitle')}
              </h1>
              <p className="max-w-xl text-muted-foreground">
                {t('downloads.pageDescription')}
              </p>
            </div>
          </div>

          <div className="grid w-full gap-4 md:grid-cols-2">
            {PLUGIN_DOWNLOADS.map((download) => (
              <Card className="w-full" key={download.id}>
                <CardHeader>
                  <CardTitle className="text-lg">
                    {t(download.titleKey)}
                  </CardTitle>
                  <CardDescription>
                    {t(download.descriptionKey, {
                      versions: download.versions.join(', '),
                    })}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {!download.url && download.unavailableKey ? (
                    <p className="text-sm text-muted-foreground">
                      {t(download.unavailableKey)}
                    </p>
                  ) : null}
                  {isRhinoHost && !canOpenExternal && download.url ? (
                    <div className="space-y-2">
                      <p className="text-sm text-muted-foreground">
                        {t('downloads.rhinoEmbedded.instructions')}
                      </p>
                      {copyFailedDownloadIds.has(download.id) ? (
                        <input
                          aria-label={t('downloads.rhinoEmbedded.linkLabel')}
                          className="w-full rounded-md border bg-background px-3 py-2 text-xs text-muted-foreground"
                          onFocus={(event) => event.currentTarget.select()}
                          readOnly
                          value={download.url}
                        />
                      ) : null}
                    </div>
                  ) : null}
                  <Button
                    onClick={() => handleDownload(download)}
                    className="w-full"
                    disabled={!download.url}
                  >
                    <Download className="size-4" />
                    {isRhinoHost
                      ? t(
                          canOpenExternal
                            ? 'downloads.rhinoEmbedded.openInBrowser'
                            : 'downloads.rhinoEmbedded.copyLink',
                        )
                      : t('downloads.downloadButton')}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
